import ActionDialog from "@dashboard/components/ActionDialog";
import { ListFilters } from "@dashboard/components/AppLayout/ListFilters";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import TablePagination, { ListSettingsUpdate } from "@dashboard/components/TablePagination";
import TableRowLink from "@dashboard/components/TableRowLink";
import { WsmProductComplianceFragment } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { renderCollection } from "@dashboard/misc";
import { ListSettings } from "@dashboard/types";
import { TableBody, TableCell, TableFooter, TableHead } from "@material-ui/core";
import { Box, Button, Checkbox, Skeleton, Text } from "@saleor/macaw-ui-next";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import { complianceMessages, sectionMessages } from "../../messages";
import { wsmComplianceUrl } from "../../urls";
import { formatStateCodes } from "../../utils";
import WsmOneLine from "../WsmOneLine/WsmOneLine";
import WsmProductField from "../WsmProductField/WsmProductField";
import WsmYesNo from "../WsmYesNo/WsmYesNo";

const numberOfColumns = 4;

export interface WsmComplianceListPageProps {
  compliances: WsmProductComplianceFragment[] | undefined;
  query: string;
  prop65Only: boolean;
  settings: ListSettings;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onQueryChange: (query: string) => void;
  onProp65OnlyChange: (prop65Only: boolean) => void;
  onUpdateListSettings: ListSettingsUpdate;
  onNextPage: () => void;
  onPreviousPage: () => void;
}

export const WsmComplianceListPage = ({
  compliances,
  query,
  prop65Only,
  settings,
  hasNextPage,
  hasPreviousPage,
  onQueryChange,
  onProp65OnlyChange,
  onUpdateListSettings,
  onNextPage,
  onPreviousPage,
}: WsmComplianceListPageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const [setUpOpen, setSetUpOpen] = useState(false);
  const [product, setProduct] = useState("");

  return (
    <ListPageLayout>
      <TopNav title={intl.formatMessage(sectionMessages.complianceListTitle)}>
        <Box display="flex" alignItems="center" gap={3}>
          <Button onClick={() => setSetUpOpen(true)} data-test-id="wsm-compliance-list-set-up">
            {intl.formatMessage(complianceMessages.setUp)}
          </Button>
        </Box>
      </TopNav>
      <DashboardCard data-test-id="wsm-compliance-list">
        <DashboardCard.Content paddingX={0}>
          {/* The served filter takes product, prop65 and search and nothing
              else, so this list cannot be inverted into the coverage queue a
              merchant actually wants ("what have I NOT covered") without a
              server-side filter. Until there is one, say what the list is. */}
          <Box paddingX={6} paddingTop={4}>
            <Text size={2} color="default2" data-test-id="wsm-compliance-list-scope">
              {intl.formatMessage(complianceMessages.listSubtitle)}
            </Text>
          </Box>
          <Box paddingTop={4} paddingBottom={2}>
            <ListFilters
              type="expression-filter"
              hideFilters
              initialSearch={query}
              searchPlaceholder={intl.formatMessage(sectionMessages.searchPlaceholder)}
              onSearchChange={onQueryChange}
              actions={
                <Checkbox
                  data-test-id="wsm-compliance-prop65-filter"
                  name="prop65Only"
                  checked={prop65Only}
                  onCheckedChange={checked => onProp65OnlyChange(!!checked)}
                >
                  <Text size={3} whiteSpace="nowrap">
                    {intl.formatMessage(complianceMessages.prop65Only)}
                  </Text>
                </Checkbox>
              }
            />
          </Box>
          <ResponsiveTable>
            <TableHead>
              <TableRowLink>
                <TableCell>{intl.formatMessage(complianceMessages.colProduct)}</TableCell>
                <TableCell>{intl.formatMessage(complianceMessages.colProp65)}</TableCell>
                <TableCell>
                  {intl.formatMessage(complianceMessages.colRestrictedStates)}
                </TableCell>
                <TableCell>{intl.formatMessage(complianceMessages.colZones)}</TableCell>
              </TableRowLink>
            </TableHead>
            <TableBody>
              {renderCollection(
                compliances,
                compliance => (
                  <TableRowLink
                    key={compliance ? compliance.id : "skeleton"}
                    hover={!!compliance}
                    href={compliance ? wsmComplianceUrl(compliance.product.id) : undefined}
                    data-test-id="wsm-compliance-row"
                  >
                    <TableCell>
                      {compliance ? (
                        <WsmOneLine>{compliance.product.name}</WsmOneLine>
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell>
                      {compliance ? (
                        <WsmYesNo value={compliance.prop65} tone="warning" />
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell>
                      {compliance ? (
                        formatStateCodes(compliance.restrictedStates) ||
                        intl.formatMessage(complianceMessages.none)
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                    {/* No zones named is not "none": the product ships
                        everywhere the store does. */}
                    <TableCell>
                      {compliance ? (
                        compliance.includeShippingZones.map(zone => zone.name).join(", ") ||
                        intl.formatMessage(complianceMessages.noZoneLimit)
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                  </TableRowLink>
                ),
                () => (
                  <TableRowLink data-test-id="wsm-compliance-list-empty">
                    <TableCell colSpan={numberOfColumns}>
                      <Box paddingY={6}>
                        {intl.formatMessage(complianceMessages.noCompliance)}
                      </Box>
                    </TableCell>
                  </TableRowLink>
                ),
              )}
            </TableBody>
            <TableFooter>
              <TableRowLink>
                <TablePagination
                  colSpan={numberOfColumns}
                  settings={settings}
                  onUpdateListSettings={onUpdateListSettings}
                  hasNextPage={hasNextPage}
                  hasPreviousPage={hasPreviousPage}
                  onNextPage={onNextPage}
                  onPreviousPage={onPreviousPage}
                />
              </TableRowLink>
            </TableFooter>
          </ResponsiveTable>
        </DashboardCard.Content>
      </DashboardCard>

      {/* A compliance row is keyed by its product, so adding one is picking
          the product. There is no create mutation: the card upserts. */}
      <ActionDialog
        open={setUpOpen}
        confirmButtonState="default"
        disabled={!product}
        title={intl.formatMessage(complianceMessages.setUpTitle)}
        onClose={() => setSetUpOpen(false)}
        onConfirm={() => navigate(wsmComplianceUrl(product))}
      >
        <WsmProductField value={product} onChange={setProduct} />
      </ActionDialog>
    </ListPageLayout>
  );
};

export default WsmComplianceListPage;
